import { ROLE_META, S, COMPANY_SETTINGS } from '../../utils/constants';

/**
 * Shell Layout Component
 * Main app wrapper with sidebar navigation and mobile header
 */
export function Shell({ currentUser, page, setPage, onLogout, children }) {
  const rm = ROLE_META[currentUser.role];

  // Build navigation items based on role
  const navItems = [
    { key: "contacts", icon: "🍽️", label: "جهات الاتصال" },
    
    // Commission pages (Marketer & Rep)
    ...((currentUser.role === "marketer" || currentUser.role === "rep")
      ? [{ key: "wallet", icon: "💰", label: "محفظتي" }]
      : []),
    
    // Financial reports (Admin & Sales Manager)
    ...((currentUser.role === "admin" || currentUser.role === "sales_manager")
      ? [{ key: "financial", icon: "📊", label: "التقارير المالية" }]
      : []),
    
    // KPI & Performance (Admin & Sales Manager) - NEW!
    ...((currentUser.role === "admin" || currentUser.role === "sales_manager")
      ? [{ key: "kpi", icon: "📈", label: "مؤشرات الأداء" }]
      : []),
    
    // Settings (Admin & Sales Manager only)
    ...((currentUser.role === "admin" || currentUser.role === "sales_manager")
      ? [{ key: "settings", icon: "⚙️", label: "الإعدادات" }]
      : []),
  ];

  return (
    <div style={{
      display: "flex",
      minHeight: "100vh",
      direction: "rtl",
      fontFamily: "'Cairo',sans-serif",
      background: S.bg,
      color: "#e2e8f0"
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;900&display=swap" rel="stylesheet"/>
      
      {/* Global Styles */}
      <style>{`
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        ::-webkit-scrollbar {
          width: 4px;
        }
        ::-webkit-scrollbar-thumb {
          background: #162035;
          border-radius: 4px;
        }
        @keyframes sIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @media (max-width: 768px) {
          .sidebar-desktop {
            display: none !important;
          }
          .mobile-header {
            display: flex !important;
          }
          .main-content {
            padding: 16px !important;
          }
        }
      `}</style>
      
      {/* Mobile Header */}
      <div className="mobile-header" style={{
        display: "none",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 56,
        background: "#080f1e",
        borderBottom: "1px solid #112030",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 16px",
        zIndex: 100
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {COMPANY_SETTINGS.companyLogo ? (
            <img 
              src={COMPANY_SETTINGS.companyLogo} 
              alt="logo" 
              style={{
                height: 40,
                width: 40,
                borderRadius: "50%",
                objectFit: "cover",
                border: "2px solid #112030"
              }} 
              onError={e => { e.target.style.display = "none"; }}
            />
          ) : (
            <span style={{ color: "#f1f5f9", fontWeight: 900, fontSize: 14 }}>
              {COMPANY_SETTINGS.companyName}
            </span>
          )}
        </div>
        <button 
          onClick={onLogout} 
          style={{
            background: "transparent",
            border: "1px solid #112030",
            borderRadius: 8,
            padding: "6px 12px",
            color: "#3d5470",
            cursor: "pointer",
            fontSize: 12,
            fontFamily: "'Cairo',sans-serif"
          }}
        >
          خروج
        </button>
      </div>
      
      {/* Desktop Sidebar */}
      <div className="sidebar-desktop" style={{
        width: 228,
        background: "#080f1e",
        borderLeft: "1px solid #112030",
        display: "flex",
        flexDirection: "column",
        padding: "20px 14px",
        flexShrink: 0
      }}>
        {/* Logo */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 26,
          paddingBottom: 18,
          borderBottom: "1px solid #112030"
        }}>
          {COMPANY_SETTINGS.companyLogo ? (
            <img 
              src={COMPANY_SETTINGS.companyLogo} 
              alt="logo" 
              style={{
                height: 48,
                width: 48,
                borderRadius: "50%",
                objectFit: "cover",
                border: "2px solid #112030"
              }} 
              onError={e => { e.target.style.display = "none"; }}
            />
          ) : (
            <span style={{ color: "#f1f5f9", fontWeight: 900, fontSize: 15 }}>
              {COMPANY_SETTINGS.companyName}
            </span>
          )}
        </div>

        {/* Navigation */}
        <nav style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 4
        }}>
          {navItems.map(item => (
            <button 
              key={item.key} 
              onClick={() => setPage(item.key)} 
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 14px",
                borderRadius: 10,
                border: "none",
                background: page === item.key ? "#112030" : "transparent",
                color: page === item.key ? "#f1f5f9" : "#3d5470",
                cursor: "pointer",
                fontFamily: "'Cairo',sans-serif",
                fontSize: 13,
                fontWeight: 700,
                textAlign: "right",
                borderRight: page === item.key 
                  ? `3px solid ${COMPANY_SETTINGS.primaryColor}` 
                  : "3px solid transparent",
                transition: "all 0.15s"
              }}
            >
              {item.icon} {item.label}
            </button>
          ))}
        </nav>

        {/* User Card */}
        <div style={{
          borderTop: "1px solid #112030",
          paddingTop: 14
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 10
          }}>
            <div style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: rm.color + "22",
              border: `1px solid ${rm.color}44`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 15,
              flexShrink: 0
            }}>
              {rm.icon}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{
                color: "#e2e8f0",
                fontSize: 12,
                fontWeight: 700,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}>
                {currentUser.name}
              </div>
              <div style={{ color: rm.color, fontSize: 10, marginTop: 1 }}>
                {rm.label}
              </div>
            </div>
          </div>
          <button 
            onClick={onLogout} 
            style={{
              width: "100%",
              padding: "7px 0",
              background: "transparent",
              border: "1px solid #112030",
              borderRadius: 9,
              color: "#3d5470",
              cursor: "pointer",
              fontFamily: "'Cairo',sans-serif",
              fontSize: 12
            }}
          >
            ↩ تسجيل الخروج
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main className="main-content" style={{
        flex: 1,
        padding: "26px 28px",
        overflowX: "auto",
        marginTop: 0
      }}>
        {children}
      </main>
    </div>
  );
}
